<template>
  <div class="user-phonenumber" v-on-clickaway="closeWithoutSubmit">
    <transition name="fadeInRight" mode="out-in">
      <div class="current-number" v-if="!isEdit" @click="setEdit" key="showNumber">
        <p v-if="!isPendingConfirmation && number.length < 8" class="pointer link">{{ $t('vue2.sms.add_number') }}</p>
        <div class="current-number__number flex align-center" v-if="number.length > 7">
          {{ number }}
        </div>
      </div>
      <div class="add-number" v-else key="editNumber">
        <div class="input-group hint" :class="hintClass"
             :aria-label="$t('vue2.sms.number_must_be_8_digits')">
          <div class="flex-group">
            <div class="flag__input-lookalike active-country">
              <img :src="activeCountry.flag" class="country-flag" alt=""/>+{{ activeCountry.countryCode }}
              <DDropdown>
                <span class="icon-arrow-down5" slot="d-dropdown__icon"></span>
                <ul slot="list">
                  <li class="country-select__item" v-for="country in countries" :key="country.countryCode"
                      @click="setActiveCountry(country)">
                    <div class="country-select__item__name"><img :src="country.flag" class="country-flag" alt=""/>
                      <p>{{ country.name }}</p></div>
                    +{{ country.countryCode }}
                  </li>
                </ul>
              </DDropdown>
            </div>
            <input type="phonenumber"
                   class="input_phonenumber"
                   ref="inputfield"
                   autocomplete="off"
                   @keypress="inputKeypress($event)"
                   @keyup="inputKeyup"
                   v-model="number"
                   :placeholder="$t('vue2.sms.add_number')"
                   :class="{'input--error': showWarning}"
                   @keyup.submit="keyPressEnter"
                   @keyup.esc="closeWithoutSubmit"/>
            <button class="submit-button"
                    v-if="isNumberChanged"
                    :aria-label="$t('vue2.sms.number_must_be_8_digits')"
                    @click="submitNumber"
                    @mouseenter="onMouseOver"
                    @mouseleave="onMouseLeave"
                    :class="[
                  {'submit-button--disabled': !isValidNumber && !isNumberBeingDeleted},
                  {'submit-button--delete': isNumberBeingDeleted}]">
              <span v-show="isNumberBeingDeleted">{{ $t('vue2.common.delete') }} </span>
              <span v-show="!isNumberBeingDeleted">{{ $t('vue2.common.save') }}</span>
            </button>
            <button v-else class="close-button"
                    @click="closeWithoutSubmit('button')">
              {{ $t('vue2.common.close') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import {mixin as clickaway} from 'vue-clickaway'
import DDropdown from '../common/DDropdown'

export default {
  name: 'SmsAddNumber',
  components: {DDropdown},
  props: {
    userid: {
      type: Number,
      required: true
    },
    hint: {
      type: String,
      default: 'hint--top'
    },
    isEditFromStart: {
      type: Boolean,
      default: false
    },
    updateThisUser: {
      type: Boolean,
      default: false
    }
  },
  mixins: [clickaway],
  data () {
    return {
      originalNumber: null,
      number: null,
      isEdit: false,
      showWarning: false,
      timeout: null,
      user: null,
      activeCountry: null,
      countries: [
        {name: 'Danmark', countryCode: '45', code: 'dk', flag: 'https://zen.docendo.co/flags/dk.svg'},
        {name: 'Norge', countryCode: '47', code: 'no', flag: 'https://zen.docendo.co/flags/no.svg'},
        {name: 'United States', countryCode: '1', code: 'us', flag: 'https://zen.docendo.co/flags/us.svg'}
      ]
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    loggedInUser () {
      return this.$store.state.user
    },
    isLoggedInUser () {
      return this.loggedInUser.id === this.user.id
    },
    hintClass () {
      return this.showWarning ? this.hint + ' hint--always' : ''
    },
    allUsersInHash () {
      return this.$store.getters.allUsersInHash
    },
    allUsersInCalIdHash () {
      return this.$store.getters.allUsersInCalIdHash
    },
    isValidNumber () {
      if (this.isUSA) {
        return true
      }
      if (this.number && this.number.length === 8) {
        this.showWarning = false
      }
      return this.number.length === 8 || (this.isNumberBeingDeleted && this.number.length === 0)
    },
    isNumberChanged () {
      return this.number !== this.originalNumber
    },
    isNumberBeingDeleted () {
      return this.number.length === 0 && (this.number !== this.originalNumber)
    },
    isPendingConfirmation () {
      return this.allUsersInHash[this.userid].unconfirmed_phone
    },
    isUSA () {
      return this.activeCountry.code === 'us'
    }
  },
  methods: {
    setActiveCountry (country) {
      this.activeCountry = country
    },
    onMouseOver () {
      if (!this.isValidNumber) {
        this.showWarning = true
      }
    },
    onMouseLeave () {
      this.showWarning = false
    },
    keyPressEnter () {
      this.submitNumber()
    },
    setEdit () {
      this.isEdit = true
      setTimeout(_ => {
        if (this.$refs.inputfield) {
          this.$refs.inputfield.focus()
        }
      }, 400)
    },
    closeEdit () {
      this.isEdit = false
    },
    inputKeypress (evt) {
      this.isNumber(evt)
    },
    isNumber (evt) {
      var charCode = (evt.which) ? evt.which : evt.keyCode
      if (charCode === 13) {
        this.keyPressEnter()
      }
      if ((charCode > 31 && (charCode < 48 || charCode > 57) && (charCode !== 9)) || (!this.isUSA && this.number.length === 8)) {
        evt.preventDefault()
      } else {
        return true
      }
    },
    setNumber () {
      this.user = this.allUsersInHash[this.userid]
      let num = ''
      // remove 45 or 44 from the start if not USA
      if (this.user.phone) {
        num = !this.isUSA && (this.user.phone.substr(0, 2) === this.activeCountry.countryCode) ? this.user.phone.replace(this.activeCountry.countryCode, '') : this.user.phone
      }
      this.originalNumber = num
      this.number = this.originalNumber
    },
    inputKeyup () {
      this.showWarning = false
    },
    closeWithoutSubmit (val) {
      if (this.isEditFromStart) {
        if (val === 'button') {
          this.$emit('close')
        }
        return
      }
      this.number = this.originalNumber
      this.closeEdit()
    },
    submitNumber () {
      if (!this.isValidNumber) {
        setTimeout(_ => {
          this.showWarning = true
        }, 300)
        return
      }
      if (this.isNumberBeingDeleted) {
        this.closeEdit()
        docendo.deleteUserPhone(window.SCHOOL.id, this.userid, payload => {
          eventbus.toast = {text: this.$t('vue2.sms.number_deleted'), success: true}
          this.$store.commit('updateSchoolUser', payload)
          this.setNumber()
          this.$emit('close')
        }, failure => {
          eventbus.toast = {text: this.$t('vue2.sms.an_error_occured'), success: false}
        })
        return
      }
      if (this.isValidNumber && !this.isNumberBeingDeleted) {
        const num = this.activeCountry.countryCode + this.number
        let changes = {}
        changes['phone'] = num
        docendo.updateUsers(this.schoolId, this.userid, changes, success => {
          this.$store.commit('updateSchoolUser', success)
          if (this.updateThisUser) {
            const updateObj = {
              prop: 'phone',
              phone: success.phone
            }
            this.$store.commit('updateUserProp', updateObj)
          }
          eventbus.toast = {text: 'Updated', success: true}
          this.$emit('close')
        }, failure => {
          eventbus.toast = {text: this.$t('vue2.sms.an_error_occured'), success: false}
        })
        if (this.isEditFromStart) {
          this.$emit('added')
        }
        this.closeEdit()
        this.$emit('close')
      }
    }
  },
  beforeMount () {
    if (window.SCHOOL.country) {
      const idxOfCountry = this.countries.findIndex(country => country.code === window.SCHOOL.country.toLowerCase())
      this.activeCountry = idxOfCountry > -1 ? this.countries[idxOfCountry] : this.countries[0]
    }
    this.setNumber()
  },
  mounted () {
    if (this.isEditFromStart) {
      this.setEdit()
    }
  }
}
</script>


<style scoped>
@import '../../assets/css/variables.css';

.flag__input-lookalike {
  min-width: 75px;
  background-color: #ffffff;
  border: 1px solid #acb6be;
  border-width: 1px 0 1px 1px;
  border-radius: 4px 0 0 4px;
  display: flex;
  align-items: center;
  justify-content: start;
  padding: 4px;
}

.input_phonenumber {
  border-radius: 0;
}

.submit-button, .close-button {
  height: auto;
  width: 50px;
  border-radius: 0 4px 4px 0;
}

.submit-button.submit-button--delete {
  background-color: var(--danger) !important;
  border-color: #dd2e11 !important;
}

.submit-button.submit-button--cancel {
  transition: all 0.25s;
  background-color: white !important;
  color: #222222 !important;
}

.pending-confirmation {
  display: flex;
  align-items: center;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #f39c12;
}

.pending-confirmation > p {
  margin-left: 16px;
  color: #f39c12;
  margin-top: 1px;
  font-size: 10px;
  text-transform: uppercase;
}

.current-number__number {
  line-height: 1;
}

.link:hover {
  color: var(--blue);
}

.flag__danish {
  width: 15px;
  margin: 0 3px 1px 0;
  border-radius: 2px;
}

.flex-group {
  display: flex;
}

.flag__input-lookalike > span {
  display: flex;
  align-items: center;
}

.flag__input-lookalike > span svg {
  width: 18px;
  height: 12px;
}

.country-select__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 150px;
}

.country-select__item__name {
  display: flex;
  align-items: center;
}

img.country-flag {
  width: 20px;
  margin-right: 5px;
}
</style>



// WEBPACK FOOTER //
// SmsAddNumber.vue?7a5ceece